import React, { useState } from 'react';

/* Gedeelde bouwstenen voor de lijstpagina's (26-08-2026), naar het ontwerp
   dat eerst op de puttenlijst is gezet:

     - sorteren met één klik op de kolomkop, met een zichtbaar dubbel pijltje;
     - vrije-tekstkolommen krijgen een loepje in de kop dat een
       deeltreffer-zoekveldje opent ("BLE01" vindt alle percelen in Baarle);
     - categorieën zitten in een paneel rechts, aanvinken betekent tonen, en
       elke waarde toont hoeveel rijen erachter zitten;
     - alles wat filtert staat als chip in de "Gefilterd:"-regel, met kruisje.

   Gebruik: geef useLijst een spec en hang de componenten op. De spec:

     facetten   [{ key, label, waarde: rij => [waarden], toon?, noot? }]
                `toon` zet een opgeslagen waarde om naar leesbare tekst, voor
                lijsten waar de databasewaarde niet hetzelfde is als het label
                (doelen bijvoorbeeld). Weggelaten = de waarde zelf tonen.
     kopzoek    { key: { label, tekst: rij => tekst } }
     zoek       rij => [[veldnaam, tekst], ...]   (voor de grote zoekbalk)
     sorteer    { kolomkey: rij => sorteerwaarde }

   De puttenlijst heeft (nog) een eigen exemplaar van dit patroon, met extra's
   die alleen daar spelen (migratieblok-zoeken via de server, twee tabellen
   met één kop). Trek die gelijk zodra dit gedeelde stuk zich bewezen heeft. */

export const markeer = (tekst, zoek) => {
  const t = String(tekst ?? '');
  if (!zoek) return t;
  const i = t.toLowerCase().indexOf(zoek.toLowerCase());
  if (i < 0) return t;
  return (
    <>
      {t.slice(0, i)}<mark>{t.slice(i, i + zoek.length)}</mark>{t.slice(i + zoek.length)}
    </>
  );
};

const LoepIcoon = () => (
  <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2"
       strokeLinecap="round" width="13" height="13">
    <circle cx="8.5" cy="8.5" r="5.5" /><path d="M12.8 12.8 17 17" />
  </svg>
);

const PIJLEN = {
  geen: (
    <svg viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.6"
         strokeLinecap="round" strokeLinejoin="round" width="14" height="14">
      <path d="M4.2 5.4 7 2.6l2.8 2.8M4.2 8.6 7 11.4l2.8-2.8" />
    </svg>
  ),
  asc: (
    <svg viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="2"
         strokeLinecap="round" strokeLinejoin="round" width="14" height="14">
      <path d="M3.4 8.8 7 5.2l3.6 3.6" />
    </svg>
  ),
  desc: (
    <svg viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="2"
         strokeLinecap="round" strokeLinejoin="round" width="14" height="14">
      <path d="M3.4 5.2 7 8.8l3.6-3.6" />
    </svg>
  ),
};

export function useLijst(spec) {
  const [zoek, setZoek] = useState('');
  const [filters, setFilters] = useState({});
  const [kopzoek, setKopzoek] = useState({});
  const [kopOpen, setKopOpen] = useState(null);
  const [sort, setSort] = useState(null);
  const [paneelOpen, setPaneelOpen] = useState(false);

  const zoekTreffer = (rij) => {
    const q = zoek.trim();
    if (!q || !spec.zoek) return { raak: true };
    for (const [waar, v] of spec.zoek(rij)) {
      if (String(v ?? '').toLowerCase().includes(q.toLowerCase())) {
        return { raak: true, waar };
      }
    }
    return { raak: false };
  };

  const doorFilters = (rij, negeer) => {
    if (!zoekTreffer(rij).raak) return false;
    for (const f of spec.facetten || []) {
      if (f.key === negeer) continue;
      const g = filters[f.key];
      if (!g || !g.size) continue;
      if (!f.waarde(rij).some(v => g.has(v))) return false;
    }
    for (const [k, def] of Object.entries(spec.kopzoek || {})) {
      const z = kopzoek[k];
      if (z && !String(def.tekst(rij) ?? '').toLowerCase().includes(z.toLowerCase())) {
        return false;
      }
    }
    return true;
  };

  const zetFilter = (key, waarde) => {
    setFilters(prev => {
      const next = { ...prev };
      const s = new Set(next[key] || []);
      s.has(waarde) ? s.delete(waarde) : s.add(waarde);
      if (s.size) next[key] = s; else delete next[key];
      return next;
    });
  };

  const zetSort = (key) => {
    setSort(prev => (!prev || prev.key !== key) ? { key, dir: 'asc' }
      : prev.dir === 'asc' ? { key, dir: 'desc' } : null);
  };

  const filterEnSorteer = (rijen) => {
    let res = rijen.filter(r => doorFilters(r, null));
    if (sort && spec.sorteer && spec.sorteer[sort.key]) {
      const f = spec.sorteer[sort.key];
      res = [...res].sort((a, b) => {
        const va = f(a), vb = f(b);
        const c = (typeof va === 'number' && typeof vb === 'number')
          ? va - vb
          : String(va).localeCompare(String(vb), 'nl', { numeric: true });
        return sort.dir === 'asc' ? c : -c;
      });
    }
    return res;
  };

  const telWaarden = (rijen, key) => {
    const f = (spec.facetten || []).find(x => x.key === key);
    const tel = new Map();
    for (const rij of rijen) {
      if (!doorFilters(rij, key)) continue;
      for (const v of f.waarde(rij)) tel.set(v, (tel.get(v) || 0) + 1);
    }
    return [...tel.entries()].sort((a, b) =>
      b[1] - a[1] || a[0].localeCompare(b[0], 'nl', { numeric: true }));
  };

  const wisKopzoek = (k) => {
    setKopzoek(prev => { const next = { ...prev }; delete next[k]; return next; });
    setKopOpen(null);
  };

  const chips = () => {
    const uit = [];
    for (const f of spec.facetten || []) {
      if (filters[f.key]) {
        for (const v of filters[f.key]) {
          uit.push({ label: f.label, tekst: f.toon ? f.toon(v) : v,
                     weg: () => zetFilter(f.key, v) });
        }
      }
    }
    for (const [k, z] of Object.entries(kopzoek)) {
      uit.push({
        label: (spec.kopzoek || {})[k]?.label || k,
        tekst: `bevat “${z}”`,
        weg: () => wisKopzoek(k),
      });
    }
    return uit;
  };

  const aantalFilters = Object.values(filters).reduce((a, s) => a + s.size, 0)
    + Object.keys(kopzoek).length;
  const filterActief = zoek.trim() !== '' || aantalFilters > 0;

  return {
    spec, zoek, setZoek, filters, setFilters, kopzoek, setKopzoek,
    kopOpen, setKopOpen, sort, zetSort, paneelOpen, setPaneelOpen,
    zoekTreffer, doorFilters, zetFilter, filterEnSorteer, telWaarden,
    wisKopzoek, chips, aantalFilters, filterActief,
    wisAlles: () => { setFilters({}); setKopzoek({}); setKopOpen(null); },
  };
}

/* De grote zoekbalk, met optionele Filters-knop voor het paneel. */
export function ZoekBalk({ lijst, placeholder, metPaneelKnop = false }) {
  return (
    <div className="pl-balk">
      <div className="pl-zoek">
        <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.8"
             strokeLinecap="round">
          <circle cx="8.5" cy="8.5" r="5.5" /><path d="M12.8 12.8 17 17" />
        </svg>
        <input
          type="search"
          value={lijst.zoek}
          onChange={(e) => lijst.setZoek(e.target.value)}
          placeholder={placeholder}
        />
        {lijst.zoek && (
          <button className="pl-wis" title="Wissen" onClick={() => lijst.setZoek('')}>×</button>
        )}
      </div>
      {metPaneelKnop && (
        <button className="pl-filterknop" onClick={() => lijst.setPaneelOpen(v => !v)}>
          <svg viewBox="0 0 14 14" fill="currentColor">
            <path d="M1.2 1h11.6c.5 0 .8.6.5 1L8.8 7.4V12c0 .3-.15.5-.4.6l-2 1c-.4.2-.8-.1-.8-.5V7.4L1.1 2c-.3-.4 0-1 .5-1z" />
          </svg>
          Filters
          {lijst.aantalFilters > 0 && <span className="pl-badge">{lijst.aantalFilters}</span>}
        </button>
      )}
    </div>
  );
}

/* De kolomkoppenrij: label sorteert, loepje opent een deeltreffer-zoekveldje. */
export function KopRij({ lijst, kolommen }) {
  return (
    <tr>
      {kolommen.map(k => {
        const gesorteerd = lijst.sort && lijst.sort.key === k.key;
        const veldOpen = k.loep && (lijst.kopOpen === k.loep || lijst.kopzoek[k.loep]);
        return (
          <th key={k.key}>
            <span className="pl-kopcel">
              <button
                type="button"
                className={`pl-koplabel${gesorteerd ? ' gesorteerd' : ''}`}
                onClick={() => lijst.zetSort(k.key)}
                title="Klik om te sorteren"
              >
                {k.label}
                <span className="pl-pijl">
                  {gesorteerd ? PIJLEN[lijst.sort.dir] : PIJLEN.geen}
                </span>
              </button>
              {k.loep && (veldOpen ? (
                <input
                  className="pl-kopveld"
                  autoFocus={lijst.kopOpen === k.loep}
                  value={lijst.kopzoek[k.loep] || ''}
                  placeholder="Zoek…"
                  onChange={(e) => {
                    const v = e.target.value;
                    lijst.setKopzoek(prev => {
                      const next = { ...prev };
                      if (v.trim()) next[k.loep] = v.trim(); else delete next[k.loep];
                      return next;
                    });
                  }}
                  onBlur={() => { if (!lijst.kopzoek[k.loep]) lijst.setKopOpen(null); }}
                  onKeyDown={(e) => { if (e.key === 'Escape') lijst.wisKopzoek(k.loep); }}
                />
              ) : (
                <button
                  type="button"
                  className="pl-kopzoek"
                  title={`Zoek in ${k.label}`}
                  onClick={() => lijst.setKopOpen(k.loep)}
                ><LoepIcoon /></button>
              ))}
            </span>
          </th>
        );
      })}
    </tr>
  );
}

/* De "Gefilterd:"-regel met een kruisje per filter. */
export function GefilterdChips({ lijst }) {
  const chips = lijst.chips();
  if (!chips.length) return null;
  return (
    <div className="pl-chips">
      <span className="pl-chips-lbl">Gefilterd:</span>
      {chips.map((c, i) => (
        <span key={i} className="pl-fchip">
          <span className="veld">{c.label}</span>
          <b>{c.tekst}</b>
          <button onClick={c.weg} title="Dit filter weghalen">×</button>
        </span>
      ))}
      <button className="pl-alleswis" onClick={lijst.wisAlles}>Alles wissen</button>
    </div>
  );
}

/* Het paneel rechts met de categorie-facetten. Geef het de ruwe (ongefilterde)
   rijen mee: de aantallen tellen dan over alles wat de overige filters
   overlaten, zodat je ziet wat een vinkje zou opleveren. */
export function FilterPaneel({ lijst, rijen }) {
  return (
    <>
      <div
        className={`pl-sluier${lijst.paneelOpen ? ' open' : ''}`}
        onClick={() => lijst.setPaneelOpen(false)}
      />
      <aside className={`pl-paneel${lijst.paneelOpen ? ' open' : ''}`} aria-label="Filters">
        <div className="pl-paneel-kop">
          <b>Filters</b>
          <button className="pl-dicht" title="Sluiten"
                  onClick={() => lijst.setPaneelOpen(false)}>×</button>
        </div>
        <div className="pl-paneel-lijf">
          {(lijst.spec.facetten || []).map(f => {
            const waarden = lijst.telWaarden(rijen, f.key);
            const gekozen = lijst.filters[f.key] || new Set();
            return (
              <div className="pl-sectie" key={f.key}>
                <h4>{f.label}</h4>
                {f.noot && waarden.length <= 1 && <div className="pl-noot">{f.noot}</div>}
                {waarden.length === 0 && <div className="pl-noot">geen waarden</div>}
                {waarden.map(([v, n]) => (
                  <label className="pl-check" key={v}>
                    <input
                      type="checkbox"
                      checked={gekozen.has(v)}
                      onChange={() => lijst.zetFilter(f.key, v)}
                    />
                    <span>{f.toon ? f.toon(v) : v}</span>
                    <span className="n">{n}</span>
                  </label>
                ))}
              </div>
            );
          })}
        </div>
        <div className="pl-paneel-voet">
          <button className="pl-wisalles" onClick={() => lijst.setFilters({})}>Alles wissen</button>
          <button className="pl-klaar" onClick={() => lijst.setPaneelOpen(false)}>Klaar</button>
        </div>
      </aside>
    </>
  );
}
